import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PublicCardLayout } from '@/components/cards/PublicCardLayout';
import { templatePreviewData } from '@/lib/templatePreviewData';
import { VISUAL_TEMPLATES } from '@/lib/visualTemplates';
import { FRAME_KEYS, FRAME_META, CLASSIC_KEYS, type ThemeKey } from '@/lib/themes';
import { Phone } from '../ui';
import { cn } from '@/lib/utils';

/**
 * Every frame and palette, rendered through the actual public-card layout
 * in a phone, switchable live. Sample content is the Founder starter.
 */
export function FramesGallery({ className, dark }: { className?: string; dark?: boolean }) {
  const [key, setKey] = useState<ThemeKey>('editorial');
  const data = useMemo(() => templatePreviewData(VISUAL_TEMPLATES.find((t) => t.id === 'founder')!), []);
  const card = { ...data.card, theme_preset: key, photo_url: '/marketing/alex-portrait.jpg' };

  return (
    <div className={cn('grid items-center gap-10 md:grid-cols-[1fr_auto]', className)}>
      <div>
        <p className={cn('mk-kicker', dark ? 'mk-gold' : 'text-ink-muted')}>Frames · a whole design each</p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {FRAME_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => setKey(k)}
              aria-pressed={key === k}
              className={cn(
                'rounded-2xl border p-3 text-left transition-colors',
                key === k
                  ? dark
                    ? 'border-[oklch(0.8_0.13_78)] bg-white/[0.05]'
                    : 'border-ink bg-surface'
                  : dark
                    ? 'border-bone/10 hover:border-bone/30'
                    : 'border-bone-deep hover:border-ink-muted/40',
              )}
            >
              <span className={cn('block text-sm font-medium tracking-tight', dark ? 'text-bone' : 'text-ink')}>
                {FRAME_META[k].name}
              </span>
              <span className={cn('mt-0.5 block text-[11px] leading-snug', dark ? 'text-bone/55' : 'text-ink-muted')}>
                {FRAME_META[k].mood}
              </span>
            </button>
          ))}
        </div>
        <p className={cn('mk-kicker mt-6', dark ? 'mk-gold' : 'text-ink-muted')}>Palettes · classic layout, your colors</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {CLASSIC_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => setKey(k)}
              aria-pressed={key === k}
              className={cn(
                'h-9 rounded-full border px-3.5 font-mono text-[10px] uppercase tracking-widest transition-colors',
                key === k
                  ? dark
                    ? 'border-[oklch(0.8_0.13_78)] text-bone'
                    : 'border-ink text-ink'
                  : dark
                    ? 'border-bone/15 text-bone/70'
                    : 'border-bone-deep text-ink-muted',
              )}
            >
              {k}
            </button>
          ))}
          <span className={cn('h-9 rounded-full border border-dashed px-3.5 font-mono text-[10px] uppercase leading-9 tracking-widest', dark ? 'border-bone/20 text-bone/50' : 'border-bone-deep text-ink-muted')}>
            + custom
          </span>
        </div>
        <p className={cn('mt-6 max-w-md text-sm leading-relaxed', dark ? 'text-bone/65' : 'text-ink-muted')}>
          A frame changes the structure: type, proportions, mood. A palette keeps the classic layout
          and changes only colors. Pick either from the Design tab; the editor previews live.
        </p>
      </div>
      <motion.div key={key} initial={{ opacity: 0.6, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="mk-float mx-auto">
        <Phone dark={dark}>
          <div className="h-full overflow-y-auto [scrollbar-width:none]">
            <PublicCardLayout card={card} links={data.links} sections={data.sections} preview compact />
          </div>
        </Phone>
      </motion.div>
    </div>
  );
}
